/**
 * LineItemsCard — the ONE line-items + totals card for both finance drawers
 * (FinanceInvoices, FinanceApprovals). Shared on purpose: each drawer once
 * built its own totals row — one summed the net lines, the other showed the
 * stored gross, both labelled "Total" — so the same invoice displayed two
 * different totals with no VAT line reconciling them. Totals rendering lives
 * here or nowhere.
 *
 * Data honesty: only backend figures render.
 *  - With a reconciled breakdown (the reconcile engine's
 *    __reconciliation.totals) the card shows Subtotal / VAT (rate) / Total
 *    and the screen adds up.
 *  - Without one (historic rows — permanent, there is no backfill) it shows
 *    the line sum LABELLED as the line sum, then the stored gross as Total.
 *    No VAT row is invented, no figure derived: absent data shows as absent.
 *
 * All colours/sizes flow from theme/tokens.ts classes — no raw hex here.
 */
import { money } from '../../../../lib/money'

// The reconcile engine's derived breakdown (snake_case on the wire).
export interface ReconciliationTotals {
  subtotal:  number
  vatRate:   number // 0, 0.05 or 0.20
  vatAmount: number
  total:     number
}

// Loose item shape: FinanceInvoices carries jsonb-shaped Record rows, the
// Approvals payload typed ExtractedLineItems — both satisfy this.
export interface LineItemLike {
  description?: unknown
  name?:        unknown
  quantity?:    unknown
  qty?:         unknown
  amount?:      unknown
}

function TotalsRow({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div className="flex items-center gap-3 px-4 py-2 bg-surface-panel">
      <span className={`finance-thead flex-1 ${emphasis ? 'text-ink-secondary' : ''}`}>{label}</span>
      <span className={`w-24 flex-shrink-0 text-right tabular-nums text-ink-primary ${emphasis ? 'text-14 font-semibold' : 'text-13 font-medium'}`}>{value}</span>
    </div>
  )
}

export function LineItemsCard({ items, currency, total, totals }: {
  items: LineItemLike[]
  currency?: string
  /** The stored gross — finance_invoices.amount / the extractor's stated total. */
  total: number
  /** Backend-reconciled breakdown; absent for historic rows. */
  totals?: ReconciliationTotals | null
}) {
  const lineSum = items.reduce((s, li) => s + (typeof li.amount === 'number' ? li.amount : 0), 0)
  return (
    // Nested inside a .k-drawer body = tile radius (slice-2 ruling), never a
    // full 18px card-in-card; the header wears the finance-thead mono type.
    <div className="rounded-tile border border-edge-panel overflow-hidden">
      <div className="finance-thead px-4 py-2 border-b border-edge-row">Line items</div>
      {items.length === 0 ? (
        <div className="px-4 py-3 text-12 text-ink-muted">No line items.</div>
      ) : (
        <>
          {items.map((li, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-2 border-b border-edge-row-faint">
              <span className="flex-1 min-w-0 text-13 font-medium text-ink-body">{String(li.description ?? li.name ?? '—')}</span>
              <span className="w-12 flex-shrink-0 text-right text-12 text-ink-muted tabular-nums">{String(li.quantity ?? li.qty ?? '—')}</span>
              <span className="w-24 flex-shrink-0 text-right text-13 font-medium text-ink-primary tabular-nums">{typeof li.amount === 'number' ? money(li.amount, currency) : String(li.amount ?? '—')}</span>
            </div>
          ))}
          {totals ? (
            <>
              <TotalsRow label="Subtotal" value={money(totals.subtotal, currency)} />
              <TotalsRow label={`VAT (${(totals.vatRate * 100).toFixed(0)}%)`} value={money(totals.vatAmount, currency)} />
              <TotalsRow label="Total" value={money(total, currency)} emphasis />
            </>
          ) : (
            <>
              <TotalsRow label="Line items" value={money(lineSum, currency)} />
              <TotalsRow label="Total" value={money(total, currency)} emphasis />
            </>
          )}
        </>
      )}
    </div>
  )
}
